/**
 * Version management utility for Learning Companion Frontend
 * Loads version information from centralized version.json and provides
 * version-related functionality for the React application
 */

import { useEffect, useState } from 'react';
import { getApiUrl } from './apiUtils';

export interface VersionInfo {
  version: string;
  build: {
    number: string;
    date: string;
    commit: string;
    environment: string;
    branch?: string;
  };
  release: {
    name: string;
    notes: string;
    features: string[];
    breaking_changes: string[];
    bug_fixes: string[];
  };
  compatibility: {
    api_version: string;
    min_supported_version: string;
    deprecated_features: string[];
  };
  deployment: {
    last_deployed: string | null;
    deployment_target: string;
    rollback_version: string | null;
  };
}

// Default version info fallback
const defaultVersionInfo: VersionInfo = {
  version: '1.0.3-dev',
  build: {
    number: 'local',
    date: new Date().toISOString(),
    commit: 'unknown',
    environment: 'development'
  },
  release: {
    name: 'Development Build',
    notes: 'Local development version',
    features: [],
    breaking_changes: [],
    bug_fixes: []
  },
  compatibility: {
    api_version: 'v1',
    min_supported_version: '1.0.0',
    deprecated_features: []
  },
  deployment: {
    last_deployed: null,
    deployment_target: 'development',
    rollback_version: null
  }
};

/**
 * Load version information from backend API
 */
export const loadVersionFromAPI = async (): Promise<VersionInfo> => {
  try {
    const response = await fetch(getApiUrl('/version/detailed'));
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`API request failed: ${response.status}`);
  } catch (error) {
    console.warn('Failed to load version from API:', error);
    return defaultVersionInfo;
  }
};

/**
 * React hook to get version information
 */
export const useVersionInfo = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>(defaultVersionInfo);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadVersion = async () => {
      try {
        setLoading(true);
        setError(null);
        const info = await loadVersionFromAPI();
        setVersionInfo(info);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load version');
        setVersionInfo(defaultVersionInfo);
      } finally {
        setLoading(false);
      }
    };

    loadVersion();
  }, []);

  return { versionInfo, loading, error, reload: () => window.location.reload() };
};

/**
 * Format version string with build information
 */
export const formatVersionString = (versionInfo: VersionInfo): string => {
  const { version, build } = versionInfo;
  
  if (build.environment === 'production') {
    return `${version}+build.${build.number}`;
  } else {
    return `${version}-${build.environment}+build.${build.number}`;
  }
};

/**
 * Check if the application version is compatible with API
 */
export const checkCompatibility = (versionInfo: VersionInfo): boolean => {
  try {
    const currentVersion = versionInfo.version.split('-')[0]; // Remove pre-release identifiers
    const minVersion = versionInfo.compatibility.min_supported_version;
    
    // Simple semantic version comparison (for basic compatibility check)
    const current = currentVersion.split('.').map(Number);
    const minimum = minVersion.split('.').map(Number);
    
    for (let i = 0; i < 3; i++) {
      if (current[i] > minimum[i]) return true;
      if (current[i] < minimum[i]) return false;
    }
    
    return true; // Equal versions are compatible
  } catch (error) {
    console.warn('Version compatibility check failed:', error);
    return true; // Assume compatible if check fails
  }
};

/**
 * Get environment-specific styling for version display
 */
export const getEnvironmentStyle = (environment: string) => {
  const styles = {
    production: 'bg-green-100 text-green-800 border-green-200',
    staging: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    development: 'bg-blue-100 text-blue-800 border-blue-200',
    default: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return styles[environment as keyof typeof styles] || styles.default;
};

/**
 * Export version info for build time injection
 */
export const getClientVersionInfo = () => {
  return {
    version: process.env.REACT_APP_VERSION || '1.0.3-dev',
    buildTime: process.env.REACT_APP_BUILD_TIME || new Date().toISOString(),
    commit: process.env.REACT_APP_GIT_COMMIT || 'unknown',
    environment: process.env.NODE_ENV || 'development'
  };
};

// Ensure this file is treated as a module for TypeScript isolatedModules
export {};